import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { usePageMeta } from '../hooks/usePageMeta';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { MapPin, FileText, Users, HeartHandshake, Mail, Phone, CalendarCheck } from 'lucide-react';

export const ServicesPrivatePage = () => {
  const { t } = useLanguage();
  usePageMeta('servicesPrivate.audienceBadge', 'seo.privateDescription');

  const services = [
    {
      icon: MapPin,
      title: t('services.service1Title'),
      description: t('services.service1Desc'),
      features: t('services.service1Features'),
      footer: t('services.service1Footer'),
    },
    {
      icon: FileText,
      title: t('services.service2Title'),
      description: t('services.service2Desc'),
      features: t('services.service2Features'),
      footer: t('services.service2Footer'),
    },
    {
      icon: Users,
      title: t('services.service3Title'),
      description: t('services.service3Desc'),
      features: t('services.service3Features'),
      footer: t('services.service3Footer'),
    },
    {
      icon: HeartHandshake,
      title: t('services.service4Title'),
      description: t('services.service4Desc'),
      features: t('services.service4Features'),
      footer: t('services.service4Footer'),
    },
  ];

  return (
    <div className="min-h-screen" data-testid="services-private-page">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-sea-blue/5 via-white to-bronze/5 py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block bg-sea-blue/10 text-sea-blue text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              {t('servicesPrivate.audienceBadge')}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('services.title')}
            </h1>
            <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
              <p>{t('services.intro1')}</p>
              <p>{t('services.intro2')}</p>
              <p>{t('services.intro3')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card
                  key={index}
                  className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-sea-blue/30"
                >
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 rounded-lg bg-sea-blue/10 flex items-center justify-center flex-shrink-0 group-hover:bg-sea-blue group-hover:scale-110 transition-all duration-300">
                        <Icon className="h-7 w-7 text-sea-blue group-hover:text-white transition-colors" />
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-2xl text-gray-900 mb-2">
                          {service.title}
                        </CardTitle>
                        <p className="text-gray-600 text-sm mb-4">
                          {service.description}
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 mb-4">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-700">
                          <div className="w-1.5 h-1.5 rounded-full bg-bronze flex-shrink-0 mt-2" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-gray-600 text-sm italic border-t border-gray-100 pt-4">
                      {service.footer}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing note */}
      <section className="py-16 bg-gradient-to-br from-bronze/5 via-white to-sea-blue/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              {t('servicesPrivate.pricingTitle')}
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              {t('servicesPrivate.pricingNote')}
            </p>
          </div>
        </div>
      </section>

      {/* How to get started */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <Card className="border-2 border-sea-blue/20 shadow-lg">
              <CardHeader>
                <CardTitle className="text-3xl text-center text-gray-900">
                  {t('servicesPrivate.howToStartTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-center text-gray-700 text-lg">
                  {t('servicesPrivate.howToStart1')}
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                  <Link to="/booking">
                    <Button
                      size="lg"
                      className="bg-sea-blue hover:bg-sea-blue/90 text-white w-full sm:w-auto"
                      data-testid="private-book-meeting-btn"
                    >
                      <CalendarCheck className="mr-2 h-5 w-5" />
                      {t('servicesPrivate.bookMeeting')}
                    </Button>
                  </Link>
                  <a href="mailto:hello@nextstepdenmark.com">
                    <Button
                      size="lg"
                      variant="outline"
                      className="border-sea-blue text-sea-blue hover:bg-sea-blue hover:text-white w-full sm:w-auto"
                    >
                      <Mail className="mr-2 h-5 w-5" />
                      {t('services.howToStartEmail')}
                    </Button>
                  </a>
                  <a href="tel:+4550912123">
                    <Button
                      size="lg"
                      variant="outline"
                      className="border-sea-blue text-sea-blue hover:bg-sea-blue hover:text-white w-full sm:w-auto"
                    >
                      <Phone className="mr-2 h-5 w-5" />
                      {t('services.howToStartPhone')}
                    </Button>
                  </a>
                </div>

                <p className="text-center text-gray-700 text-lg pt-4">
                  {t('services.howToStart3')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-sea-blue text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {t('services.ctaTitle')}
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            {t('services.ctaSubtitle')}
          </p>
          <Link to="/booking">
            <Button size="lg" className="bg-bronze hover:bg-bronze/90 text-white">
              {t('servicesPrivate.bookMeeting')}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
